import { useState } from 'react';
import type { ViewBody } from '@ayumia/contracts';
import { evaluateViewCondition } from '@ayumia/component-runtime';
import type { FormInputs } from './assembly';
import { ApiError } from '../api/client';
import { DialogActions } from '../design-system/Overlay';
import { text } from '../app/i18n';
import { fieldLabel, fieldOf, type FieldSummary, type ObjectSummary } from './format';
import { useAppServices } from './ViewContext';

/**
 * Renderer du composant Core `Form` (59 §5.5 ; ADR-099) : sections et champs de l'instance, soumis par **une commande nommée**
 * (`submit_command`), avec la cible et `expected_version` de l'enregistrement. La validation du
 * formulaire aide la saisie ; elle n'autorise rien (59 §5.5 « Critical invariant ») — les erreurs
 * du serveur (validation, politique, conflit de version) s'affichent sur les champs.
 */

type Row = Record<string, unknown> & { id?: string; version?: number };
type FieldRef = FormInputs['sections'][number]['fields'][number];

function editable(ref: FieldRef, field: FieldSummary | undefined): boolean {
  if (ref.read_only === true) return false;
  return field?.editability === undefined || field.editability === 'editable';
}

function inputValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object' && value !== null && 'value' in value) return String((value as { value: unknown }).value ?? '');
  return String(value);
}

function parse(raw: string, field: FieldSummary | undefined, previous: unknown): unknown {
  if (raw === '') return null;
  switch (field?.type) {
    case 'number':
    case 'decimal':
      return Number(raw);
    case 'boolean':
      return raw === 'true';
    case 'quantity':
      return { value: Number(raw), unit: (previous as { unit?: string } | null)?.unit ?? field.unit };
    default:
      return raw;
  }
}

export function FormRepresentation({ view, form, object, record, onSaved, onCancel }: { view: ViewBody; form: FormInputs; object: ObjectSummary | undefined; record: Row | null; onSaved: () => void; onCancel: () => void }) {
  const services = useAppServices();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ message: string; fields: Record<string, string> } | null>(null);
  const current = { ...(record ?? {}), ...values };

  const submit = async (): Promise<void> => {
    if (form.submit_command === undefined) return;
    setSaving(true);
    setError(null);
    try {
      const input: Parameters<typeof services.client.command>[1] = { payload: values };
      if (record !== null && typeof record.id === 'string') {
        input.target = { type: view.target_object_type!, id: record.id };
        if (typeof record.version === 'number') input.expected_version = record.version;
      }
      await services.client.command(form.submit_command, input);
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const fields: Record<string, string> = {};
        for (const entry of caught.fieldErrors) fields[entry.field.replace(/^payload\./, '')] = entry.message;
        setError({ message: caught.category === 'CONFLICT' ? 'L’enregistrement a changé entre-temps : rechargez-le avant de modifier.' : caught.message, fields });
      } else setError({ message: String(caught), fields: {} });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="ay-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {error !== null && (
        <p className="ay-banner ay-banner--danger" role="alert">
          {error.message}
        </p>
      )}
      {form.sections
        .filter((section) => evaluateViewCondition(section.visible_when, { record: current, context: services.context }))
        .map((section) => (
          <fieldset key={section.section_id} className="ay-form__section" style={{ gridTemplateColumns: `repeat(${section.columns ?? 1}, minmax(0, 1fr))` }}>
            {section.title !== undefined && <legend>{text(section.title)}</legend>}
            {section.fields
              .filter((ref) => evaluateViewCondition(ref.visible_when, { record: current, context: services.context }))
              .map((ref) => {
                const field = fieldOf(object, ref.field);
                const canEdit = editable(ref, field);
                const required = ref.required === true || field?.required === true;
                const message = error?.fields[ref.field];
                const common = {
                  id: `field-${ref.field}`,
                  readOnly: !canEdit,
                  disabled: saving,
                  required,
                  'aria-invalid': message !== undefined,
                  'aria-describedby': message === undefined ? undefined : `error-${ref.field}`,
                };
                const set = (raw: string): void => setValues({ ...values, [ref.field]: parse(raw, field, current[ref.field]) });
                return (
                  <div key={ref.field} className="ay-field">
                    <label className="ay-field__label" htmlFor={`field-${ref.field}`}>
                      {fieldLabel(object, ref.field, ref.label)}
                      {required && <span aria-hidden="true"> *</span>}
                    </label>
                    {field?.type === 'enumeration' || field?.type === 'boolean' ? (
                      <select {...common} disabled={saving || !canEdit} value={inputValue(current[ref.field])} onChange={(event) => set(event.target.value)}>
                        <option value="">—</option>
                        {(field.type === 'boolean' ? ['true', 'false'] : (field.enum_values ?? [])).map((option) => (
                          <option key={option} value={option}>
                            {field.type === 'boolean' ? (option === 'true' ? 'Oui' : 'Non') : text(field.enum_labels?.[option], 'fr', option)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="ay-field__control">
                        <input
                          {...common}
                          type={field?.type === 'number' || field?.type === 'decimal' || field?.type === 'quantity' ? 'number' : field?.type === 'date' ? 'date' : 'text'}
                          value={inputValue(current[ref.field])}
                          onChange={(event) => set(event.target.value)}
                        />
                        {field?.unit !== undefined && <span className="ay-field__unit">{field.unit}</span>}
                      </span>
                    )}
                    {message !== undefined && (
                      <span id={`error-${ref.field}`} className="ay-field__error">
                        {message}
                      </span>
                    )}
                  </div>
                );
              })}
          </fieldset>
        ))}
      <DialogActions cancel={{ label: 'Annuler', onClick: onCancel, disabled: saving }} primary={{ label: 'Enregistrer', type: 'submit', busy: saving, disabled: form.submit_command === undefined || Object.keys(values).length === 0 }} />
    </form>
  );
}
